<script lang="ts">
  import { PlayButtonIcon } from './components/icons';
  import type { UseFormattedProps } from 'customer-components/lib/types';

  export let properties: UseFormattedProps;

  let showEmbed = false;

  $: video = getVideoEmbed(properties.url);
  $: hasText = properties.title.length && properties.description.length;

  const getYoutubeVideoId = (url: string) => {
    const regex =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    const match = url.match(regex);
    if (match?.[7].length == 11) {
      return match[7];
    }
    return null;
  };

  const getVimeoVideoId = (url: string) => {
    const regex =
      /(?:http:|https:|)\/\/(?:player.|www.)?vimeo\.com\/(?:video\/|embed\/|watch\?\S*v=|v\/)?(\d*)/;
    const match = url.match(regex);
    return match?.[1] || null;
  };

  const getVideoEmbed = (url: string) => {
    const youtubeVideoId = getYoutubeVideoId(url);
    if (youtubeVideoId) {
      return {
        embed: `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&si=NP4UkKkrJm4mREvX&amp;controls=1`,
        thumbnail: `https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`,
      };
    }

    const vimeoVideoId = getVimeoVideoId(url);
    if (vimeoVideoId) {
      return {
        embed: `https://player.vimeo.com/video/${vimeoVideoId}?autoplay=1`,
        thumbnail: `https://vumbnail.com/${vimeoVideoId}.jpg`,
      };
    }

    return null;
  };

  const playVideo = () => {
    if (!video) return;
    showEmbed = true;
  };
</script>

<div class="video-block">
  <div
    class="video-block-inner"
    class:is-left={properties.position == 'left' && hasText}
    class:is-center={properties.position == 'center' || !hasText}
  >
    <div class="side">
      <h1 class="title">{properties.title}</h1>
      <p class="text">{@html properties.description}</p>
    </div>
    <div class="video">
      {#if showEmbed}
        <iframe
          class="video-embed"
          src={video.embed}
          title="Video player"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen; picture-in-picture"
          allowfullscreen
        />
      {:else}
        <button
          on:click={playVideo}
          class="video-preview"
          style={video &&
            `background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${video.thumbnail})`}
        >
          <PlayButtonIcon />
          <span class="video-preview-label"> Click to see a full video </span>
        </button>
      {/if}
    </div>
  </div>
</div>

<style>
  .video-block {
    background: var(--vlt-lp-background);
  }

  .text {
    color: var(--vlt-lp-background-text);
  }

  .video-block :global(ul),
  .video-block :global(ol) {
    margin: revert;
    padding: revert;
    text-align: left;
  }

  .video-block-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: auto;
    padding: 8.75rem 0;
    max-width: 1136px;
  }

  .side {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    box-sizing: border-box;
    color: var(--vlt-lp-text);
  }

  .side .title {
    width: 30rem;
    margin: 0;
    font-size: 2.5rem;
    line-height: 3.5rem;
  }

  .side .text {
    width: 32rem;
    margin: 0;
    line-height: 1.5rem;
  }

  .video {
    width: 100%;
    max-width: 560px;
    flex-shrink: 0;
    aspect-ratio: 16 / 9;
    border-radius: 0.75rem;
    background-color: #000000;
    overflow: hidden;
  }

  .video-preview {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-position: center !important;
    background-repeat: no-repeat !important;
    background-size: cover !important;
    border: none;
    cursor: pointer;
  }

  .video-preview-label {
    position: absolute;
    bottom: 1.25rem;
    padding: 0.25rem 1.25rem;
    font-size: 14px;
    line-height: 20px;
    border-radius: 0.5rem;
    background-color: rgba(255, 255, 255, 0.8);
  }

  .video-embed {
    width: 100%;
    height: 100%;
  }

  /* Video on left */
  .video-block-inner.is-left {
    flex-direction: row-reverse;
  }

  /* Video centered */
  .video-block-inner.is-center {
    gap: 2.5rem;
    flex-direction: column;
    text-align: center;
  }

  .video-block-inner.is-center .side {
    align-items: center;
  }

  .video-block-inner.is-center .title {
    max-width: 24ch;
  }

  .video-block-inner.is-center .text,
  .video-block-inner.is-center .video {
    max-width: 752px;
  }

  .video-block-inner.is-center .text :global(*:not(li)) {
    width: fit-content;
    margin: auto;
  }

  .video-block-inner.is-center .text :global(> div) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .video-block-inner.is-center .text :global(ul),
  .video-block-inner.is-center .text :global(ol) {
    padding-left: 0;
  }

  @media screen and (max-width: 768px) {
    .video-block-inner {
      flex-direction: column;
      gap: 2.5rem;
      padding: 3.75rem 0;
    }

    .side {
      width: 100%;
      padding: 0 1.25rem;
    }

    .side .title,
    .side .text {
      width: unset;
    }

    .side .title {
      font-size: 30px;
      line-height: 2.5rem;
    }

    .video {
      max-width: calc(100% - 2.5rem);
    }

    /* Video on left (top) */
    .video-block-inner.is-left {
      flex-direction: column-reverse;
    }

    /* Video centered */
    .video-block-inner.is-center .video {
      max-width: unset;
      border-radius: 0;
    }
  }
</style>
